import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { PageHeader } from "@/components/PageHeader";
import { usePageMeta } from "@/context/PageMetaContext";
import {
  createPatientProformaFromCoverageCase,
} from "@/core/billing/billing-documents/billing-documents-store";
import { getPrescriptionById } from "@/core/clinical/clinical-store";
import { PRESCRIPTION_STATUS_LABELS } from "@/core/clinical/types";
import {
  getCoverageCase,
  reviewCoverageCase,
} from "@/core/insurance/coverage-case-store";
import { submitCoverageCaseToHub } from "@/core/insurance/hub-submission";
import {
  defaultInsuranceHubUrl,
  getActivePartnershipForInsurer,
} from "@/core/insurance/partnership-store";
import {
  getInsurerAgentById,
  getInsurerById,
} from "@/core/insurance/insurer-store";
import {
  COVERAGE_CASE_KIND_LABELS,
  COVERAGE_CASE_STATUS_LABELS,
  insurerAgentFullName,
} from "@/core/insurance/types";
import { getImagingExamById } from "@/core/imaging/imaging-store";
import { IMAGING_STATUS_LABELS } from "@/core/imaging/types";
import { getLabOrderById } from "@/core/laboratory/lab-store";
import { getPatientById, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { canValidateInsuranceCase } from "@/core/permissions";
import { routes } from "@/router/all_routes";

export function InsuranceCaseDetailPage() {
  const { caseId = "" } = useParams();
  const auth = useAuth();
  const canValidate = canValidateInsuranceCase(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rejectReason, setRejectReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [proformaBusy, setProformaBusy] = useState(false);

  const coverageCase = useMemo(() => {
    void refreshKey;
    return getCoverageCase(caseId);
  }, [caseId, refreshKey]);

  usePageMeta(
    coverageCase
      ? {
          pageTitle: `Dossier — ${coverageCase.label || coverageCase.id.slice(-8)}`,
          breadcrumbLabel: coverageCase.label || `Dossier ${coverageCase.id.slice(-8)}`,
        }
      : null,
  );

  if (!coverageCase) {
    return (
      <div className="alert alert-warning">
        Dossier introuvable. <Link to={routes.insuranceCases}>Retour</Link>
      </div>
    );
  }

  const patient = getPatientById(coverageCase.patientId);
  const insurer = getInsurerById(coverageCase.insurerId);
  const agent = coverageCase.agentId ? getInsurerAgentById(coverageCase.agentId) : null;
  const prescription = coverageCase.prescriptionId
    ? getPrescriptionById(coverageCase.prescriptionId)
    : null;
  const labOrder = coverageCase.labOrderId ? getLabOrderById(coverageCase.labOrderId) : null;
  const imagingExam = coverageCase.imagingExamId
    ? getImagingExamById(coverageCase.imagingExamId)
    : null;

  const canPatientProforma =
    coverageCase.status === "rejected" ||
    coverageCase.status === "partial" ||
    coverageCase.status === "pending";

  const handlePatientProforma = async () => {
    setProformaBusy(true);
    try {
      const doc = await createPatientProformaFromCoverageCase(caseId);
      if (!doc) {
        setMsg("Impossible de générer la proforma patient.");
        return;
      }
      setMsg(`Proforma patient ${doc.number} — envoyée à la caisse.`);
      setRefreshKey((k) => k + 1);
    } finally {
      setProformaBusy(false);
    }
  };

  const reviewerName =
    auth.user?.displayName ??
    [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(" ") ??
    "Agent assureur";

  const partnership = insurer ? getActivePartnershipForInsurer(insurer.id) : null;
  const hubUrl = defaultInsuranceHubUrl();

  const handleSubmitToHub = () => {
    void submitCoverageCaseToHub(caseId).then((result) => {
      if (!result) {
        setMsg("Partenariat hub requis — configurez-le dans Partenariats assureurs.");
        return;
      }
      if (result.via === "url" && result.error) {
        setMsg(`${result.error} Ouverture du lien d'import manuel.`);
        window.open(result.importUrl, "_blank", "noopener,noreferrer");
      } else if (result.via === "api") {
        setMsg(`Dossier envoyé au hub API (réf. ${result.submissionId.slice(-8)}).`);
      } else {
        window.open(result.importUrl, "_blank", "noopener,noreferrer");
        setMsg(`Soumis au hub (réf. ${result.submissionId.slice(-8)}).`);
      }
      setRefreshKey((k) => k + 1);
    });
  };

  const handleReview = (status: "approved" | "rejected" | "partial") => {
    if (status === "rejected" && !rejectReason.trim()) {
      setMsg("Indiquez un motif de refus.");
      return;
    }
    reviewCoverageCase(caseId, status, reviewerName, rejectReason);
    setMsg(`Dossier — ${COVERAGE_CASE_STATUS_LABELS[status]}.`);
    setRefreshKey((k) => k + 1);
  };

  return (
    <div>
      <PageHeader
        title={`Dossier ${coverageCase.id.slice(-8)}`}
        description={coverageCase.label}
        actions={
          <Link to={routes.insuranceCases} className="btn btn-outline-secondary btn-sm">
            Liste dossiers
          </Link>
        }
      />

      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Patient & assureur</h3>
            <dl className="row small mb-0">
              <dt className="col-4 text-muted">Patient</dt>
              <dd className="col-8">
                {patient ? (
                  <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                ) : (
                  "—"
                )}
              </dd>
              <dt className="col-4 text-muted">Assureur</dt>
              <dd className="col-8">{insurer?.name ?? "—"}</dd>
              <dt className="col-4 text-muted">Agent</dt>
              <dd className="col-8">
                {agent ? `${agent.ref} — ${insurerAgentFullName(agent)}` : "—"}
              </dd>
              <dt className="col-4 text-muted">N° adhérent</dt>
              <dd className="col-8">{coverageCase.memberNumber || "—"}</dd>
              <dt className="col-4 text-muted">N° police</dt>
              <dd className="col-8">{coverageCase.policyNumber || "—"}</dd>
              <dt className="col-4 text-muted">Type</dt>
              <dd className="col-8">{COVERAGE_CASE_KIND_LABELS[coverageCase.kind]}</dd>
              <dt className="col-4 text-muted">Statut</dt>
              <dd className="col-8">
                <span className={`hosp-ins-badge hosp-ins-badge--${coverageCase.status}`}>
                  {COVERAGE_CASE_STATUS_LABELS[coverageCase.status]}
                </span>
              </dd>
            </dl>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Hub assureur central</h3>
            {coverageCase.hubSubmittedAt ? (
              <p className="small mb-2">
                Soumis le {new Date(coverageCase.hubSubmittedAt).toLocaleString("fr-FR")}
                {coverageCase.hubSubmissionId && (
                  <span className="d-block font-monospace text-muted">
                    {coverageCase.hubSubmissionId}
                  </span>
                )}
              </p>
            ) : partnership ? (
              <>
                <p className="small text-muted mb-3">
                  Envoyez ce dossier au hub — l&apos;assureur valide sans accès à ce tenant.
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSubmitToHub}
                >
                  Soumettre au hub
                </button>
              </>
            ) : (
              <p className="small text-muted mb-2">
                Aucun partenariat actif —{" "}
                <Link to={routes.insurancePartnerships}>configurer le lien</Link>.
              </p>
            )}
            <a
              href={hubUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-link btn-sm px-0"
            >
              Ouvrir le hub assureur
            </a>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Validation locale (override)</h3>
            {coverageCase.status !== "pending" ? (
              <p className="small mb-0">
                Traité le{" "}
                {coverageCase.reviewedAt
                  ? new Date(coverageCase.reviewedAt).toLocaleString("fr-FR")
                  : "—"}{" "}
                par {coverageCase.reviewedBy || "—"}
                {coverageCase.rejectionReason && (
                  <span className="d-block text-danger mt-2">
                    Motif : {coverageCase.rejectionReason}
                  </span>
                )}
              </p>
            ) : canValidate ? (
              <>
                <p className="small text-muted mb-3">
                  Décision interne hôpital — la validation officielle se fait sur le hub.
                </p>
                <div className="d-flex flex-wrap gap-2 mb-3 ph-split-actions">
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => handleReview("approved")}
                  >
                    Valider
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning btn-sm"
                    onClick={() => handleReview("partial")}
                  >
                    Partielle
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleReview("rejected")}
                  >
                    Refuser
                  </button>
                </div>
                <input
                  className="form-control form-control-sm"
                  placeholder="Motif de refus (obligatoire si refus)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </>
            ) : (
              <p className="small text-muted mb-0">
                En attente de validation sur le hub assureur central.
              </p>
            )}
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Paiement patient (auto-financement)</h3>
            <p className="small text-muted mb-3">
              Si l&apos;assureur refuse ou tarde, le patient peut régler lui-même via une
              proforma partielle — sans bloquer le reste du parcours.
            </p>
            {canPatientProforma ? (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={proformaBusy}
                onClick={() => void handlePatientProforma()}
              >
                {proformaBusy ? "Génération…" : "Créer proforma patient → caisse"}
              </button>
            ) : (
              <p className="small text-muted mb-0">
                Dossier validé — facturation tiers payant en cours.
              </p>
            )}
            <Link to={routes.billingCashier} className="btn btn-link btn-sm px-0 d-block mt-2">
              Ouvrir la caisse
            </Link>
          </div>
        </div>
      </div>

      {labOrder && (
        <div className="ph-invoice-sheet mb-4">
          <h3 className="h6 fw-semibold mb-3">Demande laboratoire liée</h3>
          <p className="small mb-0">
            {labOrder.orderNumber} — {labOrder.testName} · statut{" "}
            {labOrder.status.replace("_", " ")}
            {labOrder.insurerApproval === "pending" && (
              <span className="d-block text-warning mt-1">
                Prélèvement et étiquetage possibles pendant l&apos;attente assureur.
              </span>
            )}
          </p>
        </div>
      )}

      {imagingExam && (
        <div className="ph-invoice-sheet mb-4">
          <h3 className="h6 fw-semibold mb-3">Examen imagerie lié</h3>
          <p className="small mb-0">
            {imagingExam.examNumber} — {imagingExam.examType}
            {imagingExam.bodyPart ? ` (${imagingExam.bodyPart})` : ""} · statut{" "}
            {IMAGING_STATUS_LABELS[imagingExam.status]}
            {imagingExam.insurerApproval === "pending" && (
              <span className="d-block text-warning mt-1">
                Planification possible pendant l&apos;attente assureur.
              </span>
            )}
          </p>
          <Link to={routes.imagingReports} className="btn btn-link btn-sm px-0 mt-1">
            Ouvrir les comptes-rendus
          </Link>
        </div>
      )}

      {prescription && (
        <div className="ph-invoice-sheet">
          <h3 className="h6 fw-semibold mb-3">Ordonnance liée</h3>
          <p className="small mb-2">
            Statut : {PRESCRIPTION_STATUS_LABELS[prescription.status]}
          </p>
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table mb-0">
              <thead>
                <tr>
                  <th>Médicament</th>
                  <th>Qté</th>
                  <th>Posologie</th>
                </tr>
              </thead>
              <tbody>
                {prescription.lines.map((line) => (
                  <tr key={line.id} className="ph-diag-row">
                    <td data-label="Médicament">{line.productLabel}</td>
                    <td data-label="Qté">{line.quantity}</td>
                    <td className="small" data-label="Posologie">
                      {line.instructions || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
